import useWebSocket from "react-use-websocket";
import {
    InputAudioBufferAppendCommand,
    InputAudioBufferClearCommand,
    Message,
    ResponseAudioDelta,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    SessionUpdateCommand,
    ExtensionMiddleTierToolResponse,
    ResponseInputAudioTranscriptionDelta,
    ResponseInputAudioTranscriptionCompleted
} from "@/types";

// Add to a declarations file or at the top of useRealtime.tsx
// Add this at the beginning of the file after imports
// Make the field debugging function globally accessible for external integration
declare global {
    interface Window {
        _lastFormFieldUpdate?: (field: string, value: string) => void;
        debugFormField?: (message: string, field: string, value: string, context: any, stepId: number) => void;
    }
}

type Parameters = {
    useDirectAoaiApi?: boolean; // If true, the middle tier will be skipped and the AOAI ws API will be called directly
    aoaiEndpointOverride?: string;
    aoaiApiKeyOverride?: string;
    aoaiModelOverride?: string;

    enableInputAudioTranscription?: boolean;
    onWebSocketOpen?: () => void;
    onWebSocketClose?: () => void;
    onWebSocketError?: (event: Event) => void;
    onWebSocketMessage?: (event: MessageEvent<any>) => void;

    onReceivedResponseAudioDelta?: (message: ResponseAudioDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: (message: Message) => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedExtensionMiddleTierToolResponse?: (message: ExtensionMiddleTierToolResponse) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedInputAudioTranscriptionDelta?: (message: ResponseInputAudioTranscriptionDelta) => void;
    onReceivedError?: (message: Message) => void;
    connectionId?: string | null; // Add this parameter
    onReceivedInputAudioTranscriptionFailed?: () => void;
    onReceivedInterrupt?: () => void;
    onInterruptStop?: () => void; // <-- New callback parameter
};

export default function useRealTime({
    useDirectAoaiApi,
    aoaiEndpointOverride,
    aoaiApiKeyOverride,
    aoaiModelOverride,
    enableInputAudioTranscription,
    connectionId, // Add this parameter
    onWebSocketOpen,
    onWebSocketClose,
    onWebSocketError,
    onWebSocketMessage,
    onReceivedResponseDone,
    onReceivedResponseAudioDelta,
    onReceivedResponseAudioTranscriptDelta,
    onReceivedInputAudioBufferSpeechStarted,
    onReceivedExtensionMiddleTierToolResponse,
    onReceivedInputAudioTranscriptionDelta,
    onReceivedInputAudioTranscriptionCompleted,
    onReceivedError,
    onReceivedInputAudioTranscriptionFailed,
    onReceivedInterrupt,
    onInterruptStop // <-- New callback parameter
}: Parameters) {
    const wsEndpoint = useDirectAoaiApi
        ? `${aoaiEndpointOverride}/openai/realtime?api-key=${aoaiApiKeyOverride}&deployment=${aoaiModelOverride}&api-version=2024-12-17-preview`
        : `/realtime${connectionId ? `?connection_id=${connectionId}` : ""}`; // Add connection_id to URL

    const { sendJsonMessage, getWebSocket } = useWebSocket(wsEndpoint, {
        onOpen: () => {
            //console.log(`[useRealtime] WebSocket opened with connectionId: ${connectionId}`);
            onWebSocketOpen?.();
        },
        onClose: () => onWebSocketClose?.(),
        onError: event => onWebSocketError?.(event),
        onMessage: event => onMessageReceived(event),
        shouldReconnect: () => true
    });

    const startSession = () => {
        const command: SessionUpdateCommand = {
            type: "session.update",
            session: {
                turn_detection: {
                    type: "semantic_vad",
                    eagerness: "auto" // default is 0.5; lower means less sensitive
                }
            }
        };

        if (enableInputAudioTranscription) {
            command.session.input_audio_transcription = {
                model: "whisper-1"
            };
        }
        //console.log("[useRealtime] Sending session update command:", command);
        sendJsonMessage(command);
    };

    const addUserAudio = (base64Audio: string) => {
        //console.log("useRealTime.addUserAudio: sending audio data, length:", base64Audio.length);
        const command: InputAudioBufferAppendCommand = {
            type: "input_audio_buffer.append",
            audio: base64Audio
        };

        sendJsonMessage(command);
    };

    const inputAudioBufferClear = () => {
        const command: InputAudioBufferClearCommand = {
            type: "input_audio_buffer.clear"
        };

        sendJsonMessage(command);
    };

    const onMessageReceived = (event: MessageEvent<any>) => {
        onWebSocketMessage?.(event);

        let message: Message;
        try {
            message = JSON.parse(event.data);
        } catch (e) {
            console.error("Failed to parse JSON message:", e);
            throw e;
        }

        // Log all received messages for debugging
        //console.log(`[useRealtime] Message received of type: ${message.type}`);

        switch (message.type) {
            case "response.done":
                onReceivedResponseDone?.(message as ResponseDone);
                break;
            case "response.audio.delta":
                onReceivedResponseAudioDelta?.(message as ResponseAudioDelta);
                break;
            case "response.audio_transcript.delta":
                onReceivedResponseAudioTranscriptDelta?.(message as ResponseAudioTranscriptDelta);
                break;
            case "input_audio_buffer.speech_started":
                onReceivedInputAudioBufferSpeechStarted?.(message);
                break;
            case "conversation.item.input_audio_transcription.delta":
                onReceivedInputAudioTranscriptionDelta?.(message as ResponseInputAudioTranscriptionDelta);
                break;

            case "conversation.item.input_audio_transcription.completed":
                onReceivedInputAudioTranscriptionCompleted?.(message as ResponseInputAudioTranscriptionCompleted);
                break;

            case "extension.middle_tier_tool_response":
                console.log("[useRealtime] Received tool response:", message);
                try {
                    const toolResponse = message as ExtensionMiddleTierToolResponse;
                    console.log(`[useRealtime] Tool name: ${toolResponse.tool_name}, Previous item ID: ${toolResponse.previous_item_id}`);
                    console.log(`[useRealtime] Tool result (raw): ${toolResponse.tool_result}`);

                    // Try to parse and log the tool result
                    try {
                        const parsedResult = JSON.parse(toolResponse.tool_result);
                        console.log("[useRealtime] Parsed tool result:", parsedResult);

                        // Direct update fallback through global
                        if (parsedResult.field && window._lastFormFieldUpdate) {
                            // Only call the form update function if value exists and isn't empty
                            if (parsedResult.value !== undefined && parsedResult.value !== null) {
                                console.log(`[useRealtime] Updating field "${parsedResult.field}" via global function`);
                                window._lastFormFieldUpdate(parsedResult.field, parsedResult.value);
                            }
                        }
                    } catch (parseError) {
                        console.error("[useRealtime] Failed to parse tool result JSON:", parseError);
                    }

                    onReceivedExtensionMiddleTierToolResponse?.(toolResponse);
                } catch (error) {
                    console.error("[useRealtime] Error processing tool response:", error);
                }
                break;

            case "error":
                onReceivedError?.(message);
                break;
            case "conversation.item.input_audio_transcription.failed":
                onReceivedInputAudioTranscriptionFailed?.();
                break;
            case "response.interrupt":
                // Call the interrupt callback so the UI can stop playback and clear buffers
                onReceivedInterrupt?.();
                onInterruptStop?.(); // <-- New call to stop playback
                break;
        }
    };

    function endSession() {
        // If your server recognizes “session.end” (or some custom message) to abort streaming:
        sendJsonMessage({ type: "session.end" });
        // If you want to also close the WebSocket after telling the server to end,
        // do that below in #2.
    }

    function closeWebSocket() {
        // Force-close the WS so no more data arrives
        const ws = getWebSocket();
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close(1000, "User stopped conversation");
        }
    }

    return { startSession, addUserAudio, inputAudioBufferClear, endSession, closeWebSocket };
}
